<template>
	<div class="breakdown">
		<div class="ui-widget-container">
			<h3
				:class="[{'com-mod-module-header-individual': isIndividual}, 'com-mod-module-header']"
			>Equities Breakdown</h3>
			<div class="row">
				<div class="col-md-6 breakdown-chart-legend">
					<div class="toggle-section-header">
						<a
							class="sub-header"
							href="javascript:void(0)"
							:class="{active: isSector}"
							@click="toggle('sector')"
						>Sectors</a> |
						<a
							class="sub-header"
							href="javascript:void(0)"
							:class="{active: !isSector}"
							@click="toggle('industry')"
						>Top Industries</a>
					</div>
					<div class="row adjust-down">
						<div v-if="isSector" class="col-sm-4">
							<div class="row">
								<div class="col-md-12">
									<img class="breakdown-chart" :src="equityBreakdownData.equityBreakdownChart">
								</div>
								<div class="col-md-12">
									<div class="chart-title">Equity Fund Holdings</div>
								</div>
							</div>
						</div>
						<div :class="[ {'col-sm-8': isSector}, 'col-sm-12' ]">
							<div class="row">
								<div class="col-md-12">
									<div v-if="isSector">
										<div v-if="checkForData(equityBreakdownData.sectors)">
											<breakdown-table :module-data="equityBreakdownData.sectors" />
											<div
												class="sub-info"
											>% of net assets as of {{ equityBreakdownData.sectors.asOfDate }} (updated monthly)</div>
										</div>
										<div v-else>
											<span class="com-mod-table-label">No sectors data is avaliable at this time.</span>
										</div>
									</div>
									<div v-else>
										<div v-if="checkForData(equityBreakdownData.industries)">
											<blue-bar-table
												:module-data="{data: equityBreakdownData.industries.data, includeDataField: true }"
											/>
											<div
												class="sub-info adjust-date"
											>% of net assets as of {{ equityBreakdownData.industries.asOfDate }} (updated monthly)</div>
										</div>
										<div v-else>
											<span class="com-mod-table-label">No top industries data is avaliable at this time.</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-if="isAuthorizedModule['breakdownEquityTopEquity']" class="col-md-6" id="_breakdownEquityTopEquity">
					<div class="row">
						<div class="col-md-12">
							<h4 class="com-mod-section-header">
								Top Equities
								<disclosure-superscript
									class="detail-page"
									anchor="bond_topEquity"
									:statistics="['TopHLD']"
								/>
							</h4>
						</div>
					</div>
					<div class="row">
						<div class="col-md-12">
							<div v-if="checkForData(equityBreakdownData.holdings)">
								<blue-bar-table
									:module-data="{data: equityBreakdownData.holdings.data, includeDataField: true }"
								/>
								<div
									class="sub-info adjust-date"
								>% of net assets as of {{ equityBreakdownData.holdings.asOfDate }} (updated monthly)</div>
							</div>
							<div v-else>
								<span class="com-mod-table-label">No top equities data is avaliable at this time.</span>
							</div>
						</div>
						<div class="col-md-12">
							<div class="link-container">
								<i class="afis-icon-arrow-right" />
								<a :href="quarterlyHoldingsLink">
									QUARTERLY HOLDINGS
								</a>
							</div>
						</div>
					</div>
				</div>
				<!-- this displays for managedRisk and portfolioSeries and targetdate-->
				<div v-else id="underlying-funds" class="col-md-6">
					<div
						class="additional-information"
					>Go to the underlying fund pages to see detailed holdings information</div>
					<dl>
						<template
							v-for="(item, index) in underlyingFundsDataFormatted"
							:key="`underlyingFunds_${index}`"
						>
							<dt class="objective">{{ Object.keys(item)[0] }}</dt>
							<dd>
								<ul class="funds">
									<li
										v-for="(fund, index_nexted) in item[Object.keys(item)[0]]"
										:key="`c_${index_nexted}`"
										class="fund"
									>
										<a :href="fund.url">{{ fund.UnderlyingFundName }}</a>
									</li>
								</ul>
							</dd>
						</template>
					</dl>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import BreakdownTable from '_components_/global/BreakdownTable';
import BlueBarTable from '_components_/global/BlueBarTable';
import UnderlyingFundsDataMixin from '_mixins_/UnderlyingFundsDataMixin';
export default {
	name: 'BreakdownEquity',
	components: {
		BreakdownTable,
		BlueBarTable
	},
	mixins: [UnderlyingFundsDataMixin],
	props: {
		equityBreakdownData: {
			type: Object,
			default: null
		}
	},
	data() {
		this.isSector = true;
		this.isIndustry = false;
		return {
			isSector: this.isSector,
			isIndustry: this.isIndustry
		};
	},
	computed: {
		...mapGetters([
			'sectorExposure_asOfDate',
			'sectorExposure_data',
			'sectorExposure_chart'
		]),
		isIndividual() {
			return this.$store.state.isIndividual;
		}
	},
	watch: {
		equityBreakdownData: {
			handler(val) {
				this.updateExcelData(val);
			}
		}
	},
	methods: {
		checkForData(arr) {
			return arr && arr.data && arr.data.length > 0;
		},
		toggle(string) {
			if (string === 'sector') {
				this.isSector = true;
				this.isIndustry = false;
			} else {
				this.isSector = false;
				this.isIndustry = true;
			}
		},
		updateExcelData(equityBreakdownData) {
			if (!equityBreakdownData) {
				equityBreakdownData = this.equityBreakdownData || {};
			}
			this.$EventBus.$emit('ExcelData', 'EquitiesBreakdownModule', {
				sectorsAsOfDate: `% of net assets as of ${(equityBreakdownData.sectors &&
					equityBreakdownData.sectors.asOfDate) ||
					'--'} (updated monthly)`,
				sectorsData: equityBreakdownData.sectors,
				topIndustriesAsOfDate: `% of net assets as of ${(equityBreakdownData.industries &&
					equityBreakdownData.industries.asOfDate) ||
					'--'} (updated monthly)`,
				topIndustries: equityBreakdownData.industries,
				topEquitiesAsOfDate: `% of net assets as of ${(equityBreakdownData.holdings &&
					equityBreakdownData.holdings.asOfDate) ||
					'--'} (updated monthly)`,
				topEquities:
					equityBreakdownData.holdings &&
					equityBreakdownData.holdings.data
			});
		}
	}
};
</script>

<style lang="less" scoped>
@import '~_css_/breakdown-module.less';
</style>
